import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, FileSpreadsheet, AlertTriangle, BookOpenCheck } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { CARD, Label, Loader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { downloadBlob } from "./hrUtils";

const SAL_COLS = [["order", "N°"], ["last_name", "Nom"], ["first_name", "Prénoms"], ["sexe", "Sexe"], ["birth_date", "Naissance", true], ["nationalite", "Nationalité"],
  ["poste", "Emploi"], ["qualification", "Qualification"], ["contract", "Contrat"], ["date_entree", "Entrée", true], ["date_sortie", "Sortie", true], ["titre", "Titre de travail"]];
const STG_COLS = [["order", "N°"], ["last_name", "Nom"], ["first_name", "Prénoms"], ["sexe", "Sexe"], ["birth_date", "Naissance", true], ["nationalite", "Nationalité"],
  ["date_entree", "Début", true], ["date_sortie", "Fin", true], ["tuteur", "Tuteur"], ["lieu", "Lieu"], ["titre", "Titre de travail"]];

function Missing({ r }) {
  if (!r.missing.length) return null;
  return <p className="mt-1 flex items-start gap-1 text-[11px] font-semibold text-amber-600" data-testid={`reg-missing-${r.id}`}><AlertTriangle className="mt-px h-3 w-3 shrink-0" />À compléter : {r.missing.join(", ")}</p>;
}

function RegTable({ title, cols, rows, testid, onOpen, stage }) {
  return (
    <div className={`${CARD} p-4 sm:p-6`} data-testid={testid}>
      <Label className="mb-3">{title} ({rows.length})</Label>
      {rows.length === 0 ? <p className="text-sm text-gray-400">Aucune entrée.</p> : (
        <>
          <div className="hidden overflow-x-auto lg:block">
            <table className="w-full text-left text-xs">
              <thead><tr className="border-b border-gray-100 text-gray-400">{cols.map(([k, l]) => <th key={k} className="px-2 py-2 font-semibold">{l}</th>)}</tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} onClick={() => onOpen(r.id)} data-testid={`reg-row-${r.id}`} className={`cursor-pointer border-b border-gray-50 align-top hover:bg-gray-50 ${r.missing.length ? "bg-amber-50/40" : ""}`}>
                    {cols.map(([k, , isDate]) => <td key={k} className="px-2 py-2">{isDate ? (r[k] ? fmtDate(r[k]) : "") : r[k]}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.some((r) => r.missing.length) && (
              <details className="mt-2 text-[11px] text-amber-700" data-testid={`${testid}-missing-list`}>
                <summary className="cursor-pointer font-semibold">{rows.filter((r) => r.missing.length).length} fiche(s) à compléter — voir le détail</summary>
                <div className="mt-1 space-y-0.5">{rows.filter((r) => r.missing.length).map((r) => <div key={r.id}><b>N° {r.order} {r.last_name}</b> — {r.missing.join(", ")}</div>)}</div>
              </details>
            )}
          </div>
          <div className="divide-y divide-gray-50 lg:hidden">
            {rows.map((r) => (
              <button type="button" key={r.id} onClick={() => onOpen(r.id)} data-testid={`reg-card-${r.id}`} className="block w-full py-3 text-left">
                <p className="text-sm font-semibold">N° {r.order} · {r.last_name} {r.first_name}</p>
                <p className="text-xs text-gray-400">{[r.poste || r.tuteur && `Tuteur : ${r.tuteur}`, r.contract, r.date_entree && `${stage ? "début" : "entrée"} ${fmtDate(r.date_entree)}`, r.date_sortie && `${stage ? "fin de stage" : "sortie"} ${fmtDate(r.date_sortie)}`].filter(Boolean).join(" · ")}</p>
                <Missing r={r} />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export const HrRegistre = ({ emps, onOpen }) => {
  const [sites, setSites] = useState([]);
  const [site, setSite] = useState("all");
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/sites").then((r) => setSites(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setData(null);
    api.get("/hr/registre", { params: site === "all" ? {} : { site_id: site } }).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [site, emps]);
  const dl = (fmt) => downloadBlob(`/hr/registre/export.${fmt}${site === "all" ? "" : `?site_id=${site}`}`, `registre-du-personnel.${fmt}`).catch((e) => toast.error(apiErr(e)));
  const open = (id) => { const e = emps.find((x) => x.id === id); if (e) onOpen(e); };

  return (
    <div className="space-y-4" data-testid="rh-registre">
      <div className={`${CARD} flex flex-wrap items-center justify-between gap-3 p-4 sm:p-6`}>
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-display text-lg font-bold"><BookOpenCheck className="h-5 w-5 text-[#065F46]" />Registre unique du personnel</p>
          <p className="text-xs text-gray-400">Tous les salariés, présents et partis, par ordre d'embauche. À présenter en cas de contrôle (inspection du travail, URSSAF).</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={site} onValueChange={setSite}>
            <SelectTrigger data-testid="reg-site-filter" className="w-44 rounded-full"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tous les sites</SelectItem>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid="reg-export-pdf" onClick={() => dl("pdf")} className="rounded-full bg-[#065F46] hover:bg-[#047857]"><FileDown className="mr-1 h-4 w-4" />PDF</Button>
          <Button data-testid="reg-export-xlsx" variant="outline" onClick={() => dl("xlsx")} className="rounded-full"><FileSpreadsheet className="mr-1 h-4 w-4" />Excel</Button>
        </div>
      </div>
      {!data ? <Loader /> : (
        <>
          {data.missing_count > 0 && (
            <div className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="reg-missing-banner">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{data.missing_count} fiche(s) incomplète(s) : cliquez sur une ligne pour compléter les mentions obligatoires.
            </div>
          )}
          <RegTable title="Salariés" cols={SAL_COLS} rows={data.salaries} testid="reg-salaries" onOpen={open} />
          <RegTable title="Stagiaires" cols={STG_COLS} rows={data.stagiaires} testid="reg-stagiaires" onOpen={open} stage />
        </>
      )}
    </div>
  );
};
